import {
  validateAccountNumber,
  validateAlphabetCharacters,
  validateBirthdate,
  validateEIN,
  validateEmail,
  validateExistsAndAlphabetCharacters,
  validateFullSSN,
  validateLastFourDigitsCustomer,
  validateNameField,
  validateNameFieldStrictCasing,
  validatePassword,
  validatePhone,
  validatePinCode,
  validateStateIdOrDiversLicense,
} from '../../util/form-validation';
import { convertValidationRules } from '../../hooks/useFormState';
import {
  OnlineRegistrationType,
  PersonPrimaryIdType,
} from '../../__generated__/pge-types';
import { useTranslation } from '../../hooks/useTranslation';

export enum RegistrationPageStep {
  INFO = 'info',
  VERIFY = 'verify',
}

export enum BUSINESS_REGISTRATION_TYPE {
  BusinessEntity = 'businessentity',
  UserForBusiness = 'userforbusiness',
}

export type Model = {
  email: string;
  password: string;
  phoneNumber: string;
  accountNumber: string;
  registrationType: OnlineRegistrationType;
  pinCode: string;
  primaryAccountOption: string;
  firstName: string;
  middleName: string;
  lastName: string;
  birthdate: string;
  IDType: PersonPrimaryIdType;
  IDState: string;
  IDValue: string;
  step?: RegistrationPageStep;
  availableExistingIDTypes?: PersonPrimaryIdType[]; //List of ID types available for the account
  businessRegistrationType: BUSINESS_REGISTRATION_TYPE;
  businessName: string;
  existingIDType: PersonPrimaryIdType | undefined;
  existingIDValue: string;
};

const { t } = useTranslation();

interface ValidationMap<TValue> {
  [id: string]: TValue | null;
}

export const primaryFourDigitsValidator: ValidationMap<(value?: any) => any> = {
  [PersonPrimaryIdType.Ssn]: () =>
    validateLastFourDigitsCustomer.bind(null, t('SOCIAL_SECURITY_NUMBER')),
  [PersonPrimaryIdType.Dl]: () =>
    validateLastFourDigitsCustomer.bind(
      null,
      `${t('DRIVERS_LICENSE')} / ${t('STATE_ID_CARD')}`,
    ),
  [PersonPrimaryIdType.Matr]: () =>
    validateLastFourDigitsCustomer.bind(null, t('MATRICULA_ID')),
  [PersonPrimaryIdType.Pssprt]: () =>
    validateLastFourDigitsCustomer.bind(null, t('PASSPORT')),
  [PersonPrimaryIdType.Resalien]: () =>
    validateLastFourDigitsCustomer.bind(null, t('RESIDENT_ALIEN_ID')),
  [PersonPrimaryIdType.Visa]: () =>
    validateLastFourDigitsCustomer.bind(null, t('VISA_ID')),
  [PersonPrimaryIdType.Military]: () =>
    validateLastFourDigitsCustomer.bind(null, t('MILITARY_ID')),
  [PersonPrimaryIdType.Ortrib]: () =>
    validateLastFourDigitsCustomer.bind(null, t('OREGON_TRIBAL_ID')),
  [PersonPrimaryIdType.Itin]: () =>
    validateLastFourDigitsCustomer.bind(null, t('ITIN')),
  [PersonPrimaryIdType.Duns]: () =>
    validateLastFourDigitsCustomer.bind(null, t('DUNS')),
  [PersonPrimaryIdType.Wrksch]: () =>
    validateLastFourDigitsCustomer.bind(null, t('WRKSCH')),
  [PersonPrimaryIdType.Essid]: () =>
    validateLastFourDigitsCustomer.bind(null, t('ESSID')),
  [PersonPrimaryIdType.Ein]: () =>
    validateLastFourDigitsCustomer.bind(null, t('EIN')),
};

export const fullIDValidator: ValidationMap<(value?: any) => any> = {
  [PersonPrimaryIdType.Ssn]: () => validateFullSSN,
  [PersonPrimaryIdType.Dl]: () =>
    validateStateIdOrDiversLicense.bind(null, t('DL_OR_STATE_ID')),
  [PersonPrimaryIdType.Ein]: () => validateEIN,
};

export default convertValidationRules<Model>(context => {
  const {
    primaryAccountOption,
    step,
    availableExistingIDTypes,
    IDType,
    existingIDType,
    registrationType,
    businessRegistrationType,
  } = context.values;
  if (step === RegistrationPageStep.INFO) {
    return {
      // this must match the names of the form fields
      email: validateEmail,
      password: validatePassword,
      accountNumber:
        primaryAccountOption === 'accountNumber' ? validateAccountNumber : null,
      phoneNumber:
        primaryAccountOption === 'phoneNumber' ? validatePhone : null,
      pinCode: validatePinCode,
    };
  } else if (step === RegistrationPageStep.VERIFY) {
    //Check if user has existing ID in the system and entering the last 4 digits
    if (
      availableExistingIDTypes &&
      availableExistingIDTypes?.length > 0 &&
      existingIDType !== PersonPrimaryIdType.None
    ) {
      return {
        existingIDType: validateNameFieldStrictCasing.bind(null, t('ID_TYPE')),
        existingIDValue: existingIDType
          ? primaryFourDigitsValidator[existingIDType]?.()
          : validateLastFourDigitsCustomer,
      };
    } else {
      const isBusinessRegistration =
        registrationType === OnlineRegistrationType.Commercial;
      const isBusinessEntity =
        isBusinessRegistration &&
        businessRegistrationType === BUSINESS_REGISTRATION_TYPE.BusinessEntity;

      //Validate business fields if registering as a business entity
      if (isBusinessEntity) {
        return {
          businessName: validateNameField.bind(
            null,
            t('BUSINESS_NAME_FOR_REGISTRATION'),
          ),
          IDValue: fullIDValidator[IDType]?.(),
        };
      }

      //Validate the new user fields
      return {
        firstName: validateExistsAndAlphabetCharacters.bind(
          null,
          t('FIRST_NAME'),
        ),
        middleName: validateAlphabetCharacters.bind(
          null,
          t('MIDDLE_NAME_OR_INITIAL'),
        ),
        lastName: validateExistsAndAlphabetCharacters.bind(
          null,
          t('LAST_NAME'),
        ),
        birthdate: validateBirthdate,
        IDValue: fullIDValidator[IDType]?.(),
      };
    }
  } else {
    return {};
  }
});
